import { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { useRenderGate } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'
import { buildBpmSvgForExport } from '@aytm/app/stats/page/question-views/bpm/lib/buildBpmSvgForExport'

import Chart from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/Chart'

// The projected map, given the block's view. Selecting brands on the map is a report param, so the
// selection is announced rather than held here.
//
// An export clones the drawn svg rather than asking the chart to redraw itself: the labels and the
// collision layout are direct DOM work, and a redraw would lose them.
const MdsChart = ({
  id,
  questionKey,
  questionVariants,
  batches,
  answer,
  colorTheme,
  colorGradient,
  chartLegendMode,
  chartMode,
  features,
  mdsBrandsValue,
  mdsAttributesValue,
  onModelChange,
  onChartReady
}) => {
  const containerRef = useRef(null)
  const [drawn, setDrawn] = useState(false)

  const chartRendered = useRenderGate()
  const onComplete = useCallback(() => {
    setDrawn(true)
    chartRendered()
  }, [chartRendered])

  useExportableChart({
    deps: [drawn],
    prepareForExport: async () => {
      const svgNode = containerRef.current?.querySelector(`#chart_question_${id} svg`)
      const svgString = buildBpmSvgForExport(svgNode, 'm')

      return [await svgToPngDataUrl(svgString, svgNode.width.baseVal.value, svgNode.height.baseVal.value)]
    }
  })

  return (
    <Chart
      ref={containerRef}
      questionId={id}
      questionKey={questionKey}
      questionVariants={questionVariants}
      batches={batches}
      answer={answer}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      chartMode={chartMode}
      features={features}
      mdsBrandsValue={mdsBrandsValue}
      mdsAttributesValue={mdsAttributesValue}
      colorGradient={colorGradient}
      onBrandsChange={brands => onModelChange?.({ mdsBrandsValue: brands })}
      onChartReady={onChartReady}
      onComplete={onComplete}
    />
  )
}

MdsChart.displayName = 'BpmMdsChart'

MdsChart.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  questionVariants: PropTypes.array,
  batches: PropTypes.array,
  answer: answerPropType,
  colorTheme: PropTypes.array,
  colorGradient: PropTypes.func,
  chartLegendMode: PropTypes.string,
  chartMode: PropTypes.string,
  features: PropTypes.object,
  mdsBrandsValue: PropTypes.array,
  mdsAttributesValue: PropTypes.array,
  onModelChange: PropTypes.func,
  onChartReady: PropTypes.func
}

export default MdsChart
